import React, { useState, useEffect, useRef, } from 'react';
import PropTypes from 'prop-types';
import { useSelector, } from 'react-redux'
import useRouter from 'utils/useRouter'
import clsx from 'clsx';
import uuid from 'uuid/v1';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Tooltip
} from '@material-ui/core';
import ListAltIcon from '@material-ui/icons/ListAlt';
import ArrowForwardIcon from '@material-ui/icons/ArrowForwardOutlined';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import DriveEtaIcon from '@material-ui/icons/DriveEta';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import { fetchPendingBookings, fetchBookingsToRate, fetchCancelationsPending, } from 'firebaseHelpers/bookings'
import { fetchUserNeedValidation, fetchWithdrawalsPending, } from 'firebaseHelpers/users'
import { fetchParkingsNeedValidation, } from 'firebaseHelpers/parkings'

const useStyles = makeStyles(theme => ({
  root: {},
  value: {
    fontWeight: theme.typography.fontWeightMedium
  },
  type: {
    fontWeight: theme.typography.fontWeightMedium
  }
}));

const Notifications = props => {
  const { className, ...rest } = props;

  const classes = useStyles();
  const isCancelled = useRef(false)
  const router = useRouter();
  const session = useSelector(state => state.session)
  const [nbBookingsToRate, setNbBookingsToRate] = useState(0)
  const [nbPendingBookings, setNbPendingBookings] = useState(0)
  const [nbUserNeedValidation, setNbUserNeedValidation] = useState(0)
  const [nbParkingNeedValidation, setNbParkingNeedValidation] = useState(0)
  const [nbWithdrawalsPending, setNbWithdrawalsPending] = useState(0)
  const [nbCancelationPending, setNbCancelationPending] = useState(0)

  const fetchDataAdmin = async () => {
    try {
      const nbUserNeedValidation = await fetchUserNeedValidation()
      const nbParkingNeedValidation = await fetchParkingsNeedValidation()
      const nbWithdrawalsPending = await fetchWithdrawalsPending()
      const nbCancelationPending = await fetchCancelationsPending()

      if (!isCancelled.current) {
        setNbUserNeedValidation(nbUserNeedValidation)
        setNbParkingNeedValidation(nbParkingNeedValidation)
        setNbWithdrawalsPending(nbWithdrawalsPending)
        setNbCancelationPending(nbCancelationPending)
      }
    } catch (error) {
      // setError(true)
    }
  }

  const fetchDataUser = async () => {
    try {
      const nbPendingBookings = await fetchPendingBookings(session.user)
      const nbBookingsToRate = await fetchBookingsToRate(session.user)

      if (!isCancelled.current) {
        setNbPendingBookings(nbPendingBookings)
        setNbBookingsToRate(nbBookingsToRate)
      }
    } catch (error) {
      // setError(true)
    }
  }

  useEffect(() => {
    isCancelled.current = false
    if (session.user.roles === 'admin') {
      fetchDataAdmin()
    } else if (session.user.roles === '') {
      fetchDataUser()
    }

    return () => {
      isCancelled.current = true
    }
  }, []);

  let notifications = []

  const icons = {
    rating: <ThumbUpIcon />,
    userValidate: <PersonAddIcon />,
    parkingValidate: <DriveEtaIcon />,
    withdrawal: <AccountBalanceIcon />,
    cancel: <CancelOutlinedIcon />,
    bookings: <ListAltIcon />,
  };

  if (nbPendingBookings > 0) {
    notifications = [
      ...notifications,
      {
        id: uuid(),
        value: nbPendingBookings,
        type: 'bookings',
        label: 'bookings',
        message: 'need to be validated',
        onClick: () => {
          router.history.push(`/management/bookings`);
        }
      },
    ]
  }
  if (nbBookingsToRate > 0) {
    notifications = [
      ...notifications,
      {
        id: uuid(),
        value: nbBookingsToRate,
        type: 'rating',
        label: 'bookings',
        message: 'need to be rated',
        onClick: () => {
          router.history.push(`/management/bookings`);
        }
      },
    ]
  }
  if (nbUserNeedValidation > 0) {
    notifications = [
      ...notifications,
      {
        id: uuid(),
        value: nbUserNeedValidation,
        type: 'userValidate',
        label: 'users',
        message: 'need to be validated',
        onClick: () => {
          router.history.push(`/management/users`);
        }
      },
    ]
  }
  if (nbParkingNeedValidation > 0) {
    notifications = [
      ...notifications,
      {
        id: uuid(),
        value: nbParkingNeedValidation,
        type: 'parkingValidate',
        label: 'parkings',
        message: 'need to be validated',
        onClick: () => {
          router.history.push(`/management/parkings`);
        }
      },
    ]
  }
  if (nbWithdrawalsPending > 0) {
    notifications = [
      ...notifications,
      {
        id: uuid(),
        value: nbWithdrawalsPending,
        type: 'withdrawal',
        label: 'withdrawals',
        message: 'need to be processed',
        onClick: () => {
          router.history.push(`/management/withdrawals`);
        }
      },
    ]
  }
  if (nbCancelationPending > 0) {
    notifications = [
      ...notifications,
      {
        id: uuid(),
        value: nbCancelationPending,
        type: 'cancel',
        label: 'bookings',
        message: 'need a refund',
        onClick: () => {
          router.history.push(`/management/bookings`);
        }
      }
    ]
  }

  return notifications.length > 0 && (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <List>
        {notifications.map((notification, i) => (
          <ListItem
            divider={i < notifications.length - 1}
            key={notification.id}
          >
            <ListItemIcon>{icons[notification.type]}</ListItemIcon>
            <ListItemText>
              <Typography variant="body1">
                <span className={classes.value}>{notification.value}</span>{' '}
                <span className={classes.type}>{notification.label}</span>{' '}
                {notification.message}
              </Typography>
            </ListItemText>
            <ListItemSecondaryAction>
              <Tooltip title="View">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={notification.onClick}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Card>
  );
};

Notifications.propTypes = {
  className: PropTypes.string
};

export default Notifications;
